/**
 * useInfiniteScroll - Hook for detecting when user scrolls near bottom
 */

import { useEffect, useRef } from "react";

interface UseInfiniteScrollOptions {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  threshold?: number; // Distance from bottom in pixels to trigger load
  debounceMs?: number; // Debounce time in milliseconds
}

function useInfiniteScroll({
  onLoadMore,
  hasMore,
  isLoading,
  threshold = 500,
  debounceMs = 300,
}: UseInfiniteScrollOptions) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Don't set up observer if we can't load more or are already loading
    if (!hasMore || isLoading) {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      // Clear any pending debounced calls
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      return;
    }

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // Trigger load when sentinel becomes visible (with debouncing)
        if (entry.isIntersecting) {
          // Clear any existing timer
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }

          // Set new debounced timer
          debounceTimerRef.current = setTimeout(() => {
            onLoadMore();
            debounceTimerRef.current = null;
          }, debounceMs);
        }
      },
      {
        // Trigger when element is within threshold pixels of viewport
        rootMargin: `${threshold}px`,
        threshold: 0,
      }
    );

    // Observe the sentinel element
    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [onLoadMore, hasMore, isLoading, threshold, debounceMs]);

  return sentinelRef;
}

export { useInfiniteScroll };
